import clsx from "clsx";

/* Inline styles to keep “Let’s chat” readable on dark mode with glow */
const inlineCSS = `
  /* --- Gen-Z Glow Button --- */
  .btn-glow {
    position: relative;
    border-radius: 9999px;
    background: linear-gradient(135deg, #fff, #f8f8f8);
    color: #000;
    font-weight: 600;
    overflow: hidden;
    transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.4s ease;
    isolation: isolate;
  }

  .dark .btn-glow {
    background: linear-gradient(135deg, #fafafa, #eaeaea);
    color: #000;
  }

  /* Gradient aura layer */
  .btn-glow::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: inherit;
    background: linear-gradient(120deg,
      #00f5a0,
      #00d9f5,
      #ff00b8,
      #8c30ff,
      #00f5a0);
    background-size: 300% 300%;
    filter: blur(12px);
    opacity: 0.85;
    z-index: -1;
    transition: opacity 0.4s ease, filter 0.4s ease;
    animation: genzGradient 6s ease infinite;
  }

  /* Soft inner shadow for depth */
  .btn-glow {
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.9),
      0 4px 10px rgba(0,0,0,0.1);
  }

  /* Hover: subtle float + glow intensifies */
  .btn-glow:hover {
    transform: translateY(-3px) scale(1.05) rotate(-0.5deg);
    box-shadow:
      0 6px 20px rgba(0, 0, 0, 0.15),
      0 0 12px rgba(255, 255, 255, 0.5);
  }

  /* Hover: aura brightens and pulses */
  .btn-glow:hover::before {
    opacity: 1;
    filter: blur(18px);
    animation: genzGradientFast 3s ease infinite;
  }

  /* Soft pulsing outline glow */
  .btn-glow::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.15), transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  .btn-glow:hover::after {
    opacity: 1;
    animation: pulseSoft 2s ease-in-out infinite;
  }    

  /* Gradient motion animation */
  @keyframes genzGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes genzGradientFast {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes pulseSoft {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }
`;

const base =
  "inline-flex items-center justify-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60 disabled:cursor-not-allowed";

const sizes = {
  sm: "h-10 px-4 text-sm",
  md: "h-12 px-6 text-base",
  lg: "h-14 px-7 text-base",
};

const variants = {
  solid:
    "bg-[--color-primary] text-black hover:shadow-[0_0_28px_color-mix(in_oklab,var(--color-primary)_45%,transparent)] active:translate-y-[1px]",
  outline:
    "border border-white/25 text-current hover:border-white/40 hover:bg-white/10",
  ghost:
    "text-current hover:bg-white/5",
  glow:
    "btn-glow", // handled entirely by inline CSS above
};

export default function Button({
  as,
  href,
  onClick,
  children,
  className,
  variant = "glow",
  size = "sm",
  type = "button",
  ...rest
}) {
  const Cmp = as === "a" ? "a" : "button";
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: inlineCSS }} />
      <Cmp
        href={href}
        onClick={onClick}
        type={as === "a" ? undefined : type}
        className={clsx(base, sizes[size], variants[variant], "text-black dark:text-black", className)}
        {...rest}
      >
        {children}
      </Cmp>
    </>
  );
}